import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// 2026 Trending Assets: Floor-to-ceiling glass and textured louvres
const verticalImages = {
  hero: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80",
  patio: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=800&q=80",
  detail: "https://images.unsplash.com/photo-1617103023188-fb6c30d85744?auto=format&fit=crop&w=600&q=80",
};

function VerticalBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-slate-900 font-sans">
      <Helmet>
        <title>Vertical Blinds UK | Modern Patio & Bi-Fold Door Blinds</title>
        <meta
          name="description"
          content="Shop modern Vertical Blinds in the UK. Perfect for large floor-to-ceiling windows and patio doors. Discover 2026's soft-touch fabrics and cordless safety designs."
        />
        <meta name="keywords" content="Vertical Blinds UK, Patio Door Blinds, Bi-fold Door Blinds, Replacement Vertical Slats, Modern Vertical Window Treatments, Large Window Blinds" />
      </Helmet>

      {/* --- Section 1: Hero - Scaling the Design --- */}
      <header className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <div className="order-2 lg:order-1">
          <span className="text-sky-600 font-bold tracking-widest uppercase text-sm mb-2 block">Versatile & Grand</span>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight tracking-tight text-slate-900">
            Elevate Your <span className="text-sky-600">Wide Windows.</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Standard blinds often struggle with scale. <strong>Vertical Blinds</strong> thrive on it. Whether you are dressing a sprawling conservatory or modern bi-fold doors, our 2026 collection offers a sophisticated architectural line that enhances the height of your room.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="bg-slate-900 text-white px-10 py-4 rounded-full font-bold hover:bg-sky-700 transition-all shadow-xl">
              Design My Vertical Blinds
            </button>
            <Link to="/measuring-guide" className="px-10 py-4 border-2 border-slate-200 rounded-full font-bold hover:bg-slate-50 transition text-center">
              How to Measure
            </Link>
          </div>
        </div>
        <div className="relative order-1 lg:order-2">
          <img 
            src={verticalImages.hero} 
            alt="Modern floor-to-ceiling windows with soft grey vertical blinds" 
            className="rounded-[2.5rem] shadow-2xl h-[500px] w-full object-cover"
          />
          <div className="absolute -bottom-6 right-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden md:block">
            <p className="text-3xl font-black text-sky-600">89mm / 127mm</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Standard Louvre Widths</p>
          </div>
        </div>
      </header>

      {/* --- Section 2: Performance Features (The 2026 Standard) --- */}
      <section className="bg-slate-900 text-white rounded-[3rem] p-12 md:p-20 mb-24 shadow-2xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">The Vertical Advantage</h2>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="space-y-4">
            <div className="text-sky-400 text-4xl">📐</div>
            <h3 className="text-xl font-bold">Infinite Light Control</h3>
            <p className="text-slate-400 text-sm">Rotate louvres 180 degrees to track the sun's movement, keeping your lounge cool while maintaining a view of the garden.</p>
          </div>
          <div className="space-y-4">
            <div className="text-sky-400 text-4xl">🛡️</div>
            <h3 className="text-xl font-bold">Child Safe (Wand Op)</h3>
            <p className="text-slate-400 text-sm">In 2026, we've moved beyond chains. Our wand-operated systems are cord-free and safe for the whole family.</p>
          </div>
          <div className="space-y-4">
            <div className="text-sky-400 text-4xl">🧼</div>
            <h3 className="text-xl font-bold">Individual Slats</h3>
            <p className="text-slate-400 text-sm">Damaged a single slat? No need to replace the whole blind. Simply order a single replacement louvre to keep your window looking new.</p>
          </div>
        </div>
      </section>

      {/* --- Section 3: Specialized Use Cases --- */}
      <div className="space-y-24 mb-24">
        <section className="flex flex-col md:flex-row gap-16 items-center">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-6">The Perfect Patio Solution</h2>
            <p className="text-lg text-slate-700 mb-6 leading-relaxed">
              Sliding doors and bi-folds require window treatments that provide easy access. Vertical blinds can be "stacked" to the left, right, or split in the middle, ensuring your transition from indoor to outdoor living is seamless. 
            </p>
            <p className="text-lg text-slate-700 mb-6 leading-relaxed">
               Our <strong>moisture-resistant PVC louvres</strong> are also perfect for large kitchen windows where cooking steam is a factor.
            </p>
            <Link to="/kitchen-blinds" className="text-sky-600 font-bold hover:underline">Explore Kitchen Options →</Link>
          </div>
          <img src={verticalImages.patio} alt="Large patio doors with white vertical blinds" className="w-full md:w-1/2 rounded-3xl shadow-lg" />
        </section>
      </div>

      {/* --- Section 4: 2026 Fabric Tech (SEO Deep Dive) --- */}
      <section className="max-w-4xl mx-auto py-16 border-t border-slate-100">
        <h2 className="text-3xl font-bold mb-10 text-center text-slate-900">Vertical Blinds Buying Guide</h2>
        <div className="space-y-8">
          <div className="bg-slate-50 p-8 rounded-2xl">
            <h4 className="text-xl font-bold mb-3">Blackout vs. Dim-out Verticals</h4>
            <p className="text-slate-600">While Vertical blinds have slight gaps between slats, choosing <strong>Blackout louvres</strong> significantly reduces light ingress, making them a budget-friendly option for bedrooms with large windows.</p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl">
            <h4 className="text-xl font-bold mb-3">Which louvre width is right for me?</h4>
            <p className="text-slate-600">The <strong>89mm slat</strong> is the UK standard, offering a refined, detailed look. The wider <strong>127mm slat</strong> is better for massive commercial spaces or ultra-wide glass, requiring fewer louvres and creating a bolder architectural statement.</p>
          </div>
        </div>
      </section>

      {/* --- Section 5: Internal Link Navigation & Footer --- */}
      <footer className="text-center mt-20">
        <div className="mb-12">
          <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">More from the Collection</p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/roller-blinds" className="text-slate-700 hover:text-sky-600 font-medium border-b border-transparent hover:border-sky-600 transition">Roller Blinds</Link>
            <Link to="/venetian-blinds" className="text-slate-700 hover:text-sky-600 font-medium border-b border-transparent hover:border-sky-600 transition">Venetian Blinds</Link>
            <Link to="/perfect-fit-blinds" className="text-slate-700 hover:text-sky-600 font-medium border-b border-transparent hover:border-sky-600 transition">Perfect Fit</Link>
          </div>
        </div>
        
        <div className="bg-sky-50 py-16 px-6 rounded-[3rem]">
          <h2 className="text-4xl font-bold mb-4 text-slate-900">Transform Your Glass Today</h2>
          <p className="mb-10 text-slate-600 max-w-xl mx-auto">Get that modern "hotel look" with high-quality fabrics and precision-engineered headrails.</p>
          <button className="bg-sky-600 text-white px-12 py-5 rounded-full text-xl font-bold shadow-2xl hover:bg-sky-700 transition-all hover:scale-105">
            Order Free Swatches
          </button>
        </div>
      </footer>
    </div>
  );
}

export default VerticalBlinds;